import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import Header from '../components/Header';

export default function TermsOfService() {
  const { betaConfig } = useAuth();
  const betaMode = !!betaConfig?.beta_mode;
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-2" data-testid="terms-title">Terms of Service</h1>
        <p className="text-sm text-stone-400 mb-8">Last updated: June 2026</p>

        <div className="prose prose-stone max-w-none space-y-6 text-sm leading-relaxed text-stone-600">
          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">1. About Zuriya</h2>
            <p>
              Zuriya Ltd (&quot;Zuriya&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a company registered in
              England and Wales and operates the platform at zuriya.co.uk, an online marketplace that connects
              clients (&quot;Clients&quot;) with independent beauty professionals (&quot;Providers&quot;) specialising
              in inclusive multi-cultural beauty services. We facilitate discovery, booking, and payment. We are <strong>not</strong>
              a party to the service contract between a Client and a Provider; the treatment itself is delivered by the
              Provider as an independent contractor.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">2. Eligibility</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>You must be at least 18 years old to create an account or make a booking.</li>
              <li>Providers must be legally permitted to offer beauty services in the United Kingdom and comply with all applicable licensing, hygiene, and consumer-law obligations.</li>
              <li>All information you supply must be accurate and kept up to date.</li>
              <li>Account registration requires a valid email address. You must complete email verification (via a one-time passcode) before your account is fully activated.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">3. Accounts, Security &amp; Two-Factor Authentication</h2>
            <p>You are responsible for maintaining the security of your account credentials. Zuriya is not liable for losses resulting from your failure to protect your login details or share them with others.</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Email verification:</strong> one-time passcode sent to your registered address during signup.</li>
              <li><strong>Provider verification:</strong> Provider accounts undergo a manual review (portfolio, social-media proof of work, and Stripe Connect KYC) before they are listed publicly.</li>
              <li><strong>Two-factor authentication (2FA):</strong> all users may enable email-based 2FA from the Security tab of their dashboard, which also issues ten single-use backup codes. 2FA is <strong>mandatory</strong> for accounts holding administrative privileges on the platform.</li>
              <li><strong>Device recognition:</strong> we record a device fingerprint on successful sign-in so we can alert you by email the first time your account is accessed from an unfamiliar device. You can revoke any recognised device from your dashboard.</li>
              <li>We may rate-limit, suspend, or terminate accounts that violate these Terms, engage in fraud, or create material risk to the platform or its users.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">4. Bookings &amp; Payments</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>All bookings are made through the Zuriya platform and all payments are processed by Stripe Payments UK, Ltd.</li>
              <li>Providers may require a deposit of between 20% and 100% of the service price at the time of booking, with any balance due before or at the appointment. The amount payable now and later is shown to you before you confirm.</li>
              <li><strong>No booking fee for Clients.</strong> The price shown for a service is the price the Client pays. Zuriya&apos;s fees are taken from the Provider&apos;s share and are never added to the Client&apos;s payment.</li>
              <li>Clients may also pay the full service price upfront, including any add-ons selected.</li>
              <li><strong>Guest checkout</strong> is supported, a full account is not required to make a booking. A secure magic-link is emailed to Guests so they can manage, reschedule, or cancel their booking without creating an account.</li>
              <li>
                <strong>Card on file.</strong> Where the Provider&apos;s cancellation policy is Moderate or Strict and no
                (or only a partial) deposit is collected, you will be asked to securely save a payment method so that
                late-cancellation or no-show fees can be captured on an off-session basis. Full details of how this works
                are set out in the <Link to="/refunds" className="text-[#D4AF37] underline">Refund &amp; Cancellation Policy</Link> Section 3.
              </li>
              <li>Bookings without action from either party are automatically marked &quot;Completed&quot; two hours after the scheduled end of the appointment. Clients have 48 hours thereafter to raise a dispute.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">5. Platform Fees, Commissions &amp; Pro Subscription</h2>
            <p className="mb-2">
              Zuriya deducts a platform fee (and, where applicable, a payment-processing fee) from the Provider&apos;s
              share of the amount paid by the Client before remitting the balance to the Provider. Clients are never
              charged a booking fee. The applicable rates depend on the Provider&apos;s tier and on
              whether the booking comes through the Zuriya marketplace or directly from the Provider&apos;s own share-link.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#E6E4DD] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#F9F6F0]">
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Tier</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Marketplace, new client</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Repeat client or Direct link</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Card processing</th>
                  </tr>
                </thead>
                <tbody>
                  {betaMode && (
                    <tr className="border-t border-[#E6E4DD] bg-[#FFFBEB]" data-testid="terms-beta-row">
                      <td className="py-2 px-3 font-medium">Founding Provider (Beta)</td>
                      <td className="py-2 px-3">{betaConfig?.provider_fee_percent}% (processing included)</td>
                      <td className="py-2 px-3">0%</td>
                      <td className="py-2 px-3">Included on new marketplace clients, otherwise 1.75% + 20p</td>
                    </tr>
                  )}
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-medium">Free</td>
                    <td className="py-2 px-3">15%</td>
                    <td className="py-2 px-3">0%</td>
                    <td className="py-2 px-3">1.75% + 20p</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD]">
                    <td className="py-2 px-3 font-medium">Pro (£25/month)</td>
                    <td className="py-2 px-3">8%</td>
                    <td className="py-2 px-3">0%</td>
                    <td className="py-2 px-3">1.55% + 20p</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {betaMode && (
              <p className="text-xs text-stone-500 mt-2" data-testid="terms-beta-note">
                Zuriya is currently in its Founding Provider Beta phase. During the beta no subscription applies and
                Clients pay no booking fee. A platform fee of {betaConfig?.provider_fee_percent}% is deducted from the
                Provider&apos;s payout on bookings from new marketplace Clients; this fee includes card processing, so
                no separate processing fee applies to those bookings. Bookings from repeat Clients or via a
                Provider&apos;s direct link carry no platform fee, with standard processing fees still applying. Where
                only a deposit is paid online, the platform fee is calculated on the full booking price and deducted from
                the deposit; any balance the Client pays the Provider at the appointment is not subject to a Zuriya fee.
                All approved providers are treated equivalently to Pro-tier for feature access. The Free and Pro tiers
                listed above will resume once the beta concludes, with advance notice to existing providers.
              </p>
            )}

            <ul className="list-disc pl-5 space-y-1 mt-3">
              <li><strong>Flat card processing fees.</strong> Processing fees are a flat rate across all card types (consumer, corporate, Amex, and international). 1.75% + 20p on Free and 1.55% + 20p on Pro, taken from the Provider&apos;s share of the Client&apos;s payment before payout. During the beta, processing is included in the platform fee on new marketplace Clients. This keeps payouts predictable for Providers.</li>
              <li><strong>Smart commission.</strong> Bookings made via a Client&apos;s direct link to a Provider, or made by a Client who has booked that Provider before, incur 0% platform commission on either tier. Processing fees still apply.</li>
              <li><strong>Refunds.</strong> When a Client is refunded, in full or in part, Zuriya returns its platform fee on the refunded amount. A refund to a Client is never reduced by Zuriya&apos;s fees.</li>
              <li>
                <strong>Pro subscription.</strong> Pro is billed at <strong>£25 per calendar month</strong> via Stripe
                subscription. You may cancel at any time from the Earnings tab; cancellation takes effect at the end of
                the current billing period and Zuriya does not pro-rate partial months. Pro-tier commissions apply only
                while the subscription is active; if Stripe is unable to collect a renewal, the account reverts to the
                Free tier with no loss of data.
              </li>
              <li>Prices on the platform are shown in pounds sterling (GBP) and are inclusive of VAT where applicable.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">6. Provider Payouts</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Payouts are run by Zuriya automatically. Providers cannot trigger or adjust an individual payout.</li>
              <li>Funds from a completed booking become eligible <strong>48 hours after service completion</strong>, subject to no active dispute or chargeback.</li>
              <li>
                Payout sweeps run every <strong>Monday at 04:00 UTC</strong>. A Provider is paid when their Available
                balance meets or exceeds <strong>£100</strong>; otherwise the balance rolls forward until the threshold
                is met.
              </li>
              <li>Bank transfers are executed via Stripe Connect (Express accounts) and typically arrive within 1–2 UK business days. Transfer times are determined by Stripe and your bank, not by Zuriya.</li>
              <li>Zuriya may place a Provider&apos;s payouts on hold, in whole or in part, where there is an unresolved dispute, chargeback, suspected fraud, or an HMRC or other regulatory obligation requiring it.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">7. Cancellations, Refunds &amp; Card-on-File Fees</h2>
            <p>
              Each Provider chooses one of three cancellation tiers, Flexible, Moderate, or Strict, and the tier in force
              is shown on their profile and at checkout. Full rules on refund entitlements, late-cancellation fees, no-show
              fees, off-session card-on-file capture, the 24-hour provider grace period, and the 48-hour post-completion
              dispute window are set out in the
              <Link to="/refunds" className="text-[#D4AF37] underline"> Refund &amp; Cancellation Policy</Link>, which forms
              part of these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">8. Provider Obligations</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Deliver services in line with the listing, at the time and location agreed with the Client.</li>
              <li>Act as independent contractors. Providers are not employees, workers, or agents of Zuriya; Zuriya does not direct or supervise the treatment.</li>
              <li>Maintain their own public-liability insurance, tax registration (including HMRC self-assessment or VAT where applicable), and professional compliance.</li>
              <li>Only upload portfolio images, videos, and transformations that are their own original work and for which they have all necessary consents (including from clients shown).</li>
              <li>Honour the cancellation tier they have chosen and the platform fee structure set out in Section 5.</li>
              <li>Not solicit or accept bookings off-platform for services discovered via Zuriya, and not share external contact details with a Client in order to circumvent platform fees.</li>
              <li>Not engage in discriminatory, harassing, fraudulent, or otherwise unlawful conduct.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">9. Client Obligations</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Arrive on time, disclose any allergies, sensitivities, or contraindications the Provider reasonably requests, and treat the Provider with respect.</li>
              <li>Provide honest reviews based on genuine experience.</li>
              <li>Not book with the intention of later cancelling solely to frustrate a Provider&apos;s schedule; repeated last-minute cancellations may lead to restrictions on the account.</li>
              <li>Not raise a chargeback before attempting to resolve the issue with us (see Refund Policy Section 9).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">10. Reviews</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Zuriya operates a two-way review system. Each party may submit one review per completed booking.</li>
              <li>Reviews open 24 hours after completion (to allow reflection) and close after a reasonable window.</li>
              <li>Client reviews of Providers are public on the Provider&apos;s profile and contribute to their rating; Provider reviews of Clients are visible only to the reviewing Provider and the Zuriya moderation team.</li>
              <li>Providers may post a public response to a Client review.</li>
              <li>Reviews that are abusive, defamatory, solicited in exchange for payment or discount, or manifestly false may be removed at our discretion.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">11. Referrals, Credits &amp; Waitlists</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Every registered Client receives a unique referral code. A friend who signs up and completes a first booking using the code receives a one-off £5 discount at checkout, funded by Zuriya (Provider earnings are not reduced).</li>
              <li>For every two completed referrals, the referrer is awarded a £5 platform credit which auto-applies to their next booking. Credits are non-transferable and have no cash value.</li>
              <li>Abuse of the referral programme (including self-referral, fake accounts, or bulk sign-ups) will forfeit all earned credits and may lead to account suspension.</li>
              <li><strong>Waitlists.</strong> Clients may join a waitlist for a time-slot that is already booked. If that slot becomes available the platform may contact waitlisted Clients by email (and, where enabled, SMS). Joining a waitlist does not create a booking until the Client confirms a freshly generated checkout.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">12. Content &amp; Intellectual Property</h2>
            <p>
              The Zuriya name, brand, logo, and platform design are the property of Zuriya Ltd. Providers retain ownership of
              portfolio media they upload but grant Zuriya a non-exclusive, worldwide, royalty-free licence to display,
              distribute, resize, and promote that media within and in connection with the platform, including in marketing
              materials identifying the Provider. Clients grant Zuriya a similar licence for any photos or text submitted
              with reviews.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">13. Platform Integrity</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>All bookings and payments must be processed through Zuriya.</li>
              <li>Attempting to circumvent the platform, for example by arranging direct payment, sharing external contact details in the messaging system, or discounting off-platform, is a material breach of these Terms and may result in suspension, termination, and loss of pending payouts.</li>
              <li>Social-media handles supplied at signup are used for admin verification only and are not published on public profiles.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">14. Account Deletion &amp; Data Lifecycle</h2>
            <p>
              You may request deletion of your account at any time from <strong>Dashboard → Security → Delete account</strong>.
              On confirmation the account is <strong>deactivated immediately</strong> and enters a 30-day grace period during
              which you may reactivate it by contacting support. After 30 days the account and its personal data are
              permanently anonymised. Financial records (bookings, invoices, payouts) are retained for six years where
              required under UK tax and accounting law (see
              <Link to="/privacy" className="text-[#D4AF37] underline"> Privacy Policy</Link> Section 7). Deletion cannot be
              completed while a Provider has an unresolved dispute, pending payout, or outstanding balance; these will
              be flagged at the point of request.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">15. Limitation of Liability</h2>
            <p>
              Zuriya acts as a marketplace facilitator. We are not liable for the quality or safety of services provided by
              Providers, for disputes between Clients and Providers, or for any personal injury, allergic reaction, property
              damage, or consequential loss arising from a treatment booked through the platform. To the fullest extent
              permitted by law, our aggregate liability in respect of any booking is limited to the platform fees Zuriya
              actually received from that booking. Nothing in these Terms excludes liability for fraud, death or personal
              injury caused by our negligence, or any other liability that cannot lawfully be excluded.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">16. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be notified by email and surfaced in-app
              before they take effect. Continued use of the platform after the effective date of an update constitutes
              acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">17. Governing Law</h2>
            <p>
              These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with
              them is subject to the exclusive jurisdiction of the courts of England and Wales. Nothing in this clause
              removes your ability to enforce mandatory consumer rights under the law of your place of residence.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">18. Contact</h2>
            <p>
              Questions about these Terms? Email
              <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline"> support@zuriya.co.uk</a>.
              For privacy enquiries, write to
              <a href="mailto:privacy@zuriya.co.uk" className="text-[#D4AF37] underline"> privacy@zuriya.co.uk</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
